import React, { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, PackageCheck, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Delivered-Excel Import Modal.
 *
 * Two-phase flow driven by parent (BulkPaste):
 *   Phase 1 · file uploaded → POST /bulk-paste/delivered-preview
 *   Phase 2 · buckets shown → user confirms + commits
 *
 * Rules (mirrors normal Bulk Paste):
 *   • One chat card per (agency, delivered_date) — 01.09 alag, 02.09 alag.
 *   • Duplicate LPGs (cross-agency, cross-status) auto-skipped and shown
 *     under a "Duplicate — Skipped" section per bucket.
 *   • Unmatched agencies (no matching group in DB) get a re-route
 *     dropdown so the admin can pick the right group before commit.
 */
export default function DeliveredExcelModal({
  file,
  uploaderName,
  onClose,
  onSuccess,
}) {
  const [phase, setPhase] = useState("loading");    // loading | ready | committing | done
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [availableGroups, setAvailableGroups] = useState([]);
  // group_id override keyed by bucket index — user can re-route an
  // unmatched bucket to a specific group before commit.
  const [routeOverride, setRouteOverride] = useState({});
  const [commitRes, setCommitRes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        // Load groups (needed for re-route dropdowns)
        const g = await api.get("/groups");
        if (!cancelled) setAvailableGroups(g.data || []);
        // Upload file to preview endpoint
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/bulk-paste/delivered-preview", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!cancelled) {
          setPreview(data);
          setPhase("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(formatApiError(e));
          setPhase("ready");
        }
      }
    };
    if (file) run();
    return () => {
      cancelled = true;
    };
  }, [file]);

  const resolvedGid = (bucket, idx) => routeOverride[idx] ?? bucket.group_id;

  const handleCommit = async () => {
    if (!preview) return;
    // Build buckets payload — drop buckets without a resolved group_id.
    const buckets = preview.buckets
      .map((b, idx) => ({
        group_id: resolvedGid(b, idx),
        delivered_date: b.delivered_date,
        entries: b.entries,
        aliases: b.agency_label ? [b.agency_label] : [],
      }))
      .filter((b) => b.group_id);
    // Resend buckets — one per group, un-matched agencies use the
    // routeOverride hash indexed by group_id so a single dropdown drives
    // both delivered + resend re-routing.
    const resend_buckets = (preview.resend_buckets || [])
      .map((rb) => ({
        group_id:
          rb.group_id ||
          routeOverride[`resend:${rb.agency_label || "_"}`] ||
          "",
        agency_label: rb.agency_label,
        entries: rb.entries,
      }))
      .filter((rb) => rb.group_id && (rb.entries || []).length);
    if (!buckets.length && !resend_buckets.length) {
      toast.error("Har unmatched agency ko pehle route karo.");
      return;
    }
    setPhase("committing");
    setErr("");
    try {
      const { data } = await api.post("/bulk-paste/delivered-commit", {
        buckets,
        resend_buckets,
        uploader_name: uploaderName || "",
      });
      setCommitRes(data);
      setPhase("done");
      const okCount = (data.results || []).filter(
        (r) => r.ok && ((r.sent || 0) + (r.flipped || 0) > 0)
      ).length;
      const totalFresh = (data.results || []).reduce(
        (a, r) => a + (r.sent || 0),
        0
      );
      const totalFlipped = (data.results || []).reduce(
        (a, r) => a + (r.flipped || 0),
        0
      );
      const totalResendCreated = (data.resend_results || []).reduce(
        (a, r) => a + (r.created || 0),
        0
      );
      const totalResendRetried = (data.resend_results || []).reduce(
        (a, r) => a + (r.retried || 0),
        0
      );
      toast.success(
        `${okCount} bucket(s) · ${totalFresh} fresh + ${totalFlipped} flipped · ${totalResendCreated} resend created + ${totalResendRetried} retried.`
      );
      if (onSuccess) onSuccess(data);
    } catch (e) {
      setErr(formatApiError(e));
      setPhase("ready");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="delivered-excel-modal"
    >
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-[#385C4B]" />
            <h2
              className="text-lg font-semibold text-[#1C1C19]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Delivered Excel · Import
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="delivered-modal-close"
            className="text-[#6B6B61] hover:text-[#1C1C19]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {phase === "loading" && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#385C4B]" />
              <p className="text-sm text-[#6B6B61] mt-3 font-mono">
                Parsing delivered Excel…
              </p>
            </div>
          )}

          {err && (
            <div
              className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-sm"
              data-testid="delivered-modal-error"
            >
              <AlertTriangle className="w-4 h-4 inline-block mr-1" />
              {err}
            </div>
          )}

          {phase === "ready" && preview && (
            <>
              {/* Totals bar */}
              <div className="bg-[#F5F5F0] border border-[#DCDCD3] rounded p-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Rows parsed
                  </p>
                  <p className="text-lg font-semibold text-[#1C1C19]">
                    {preview.totals.rows_parsed}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Date buckets
                  </p>
                  <p className="text-lg font-semibold text-[#1C1C19]">
                    {preview.totals.buckets}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Fresh (new card)
                  </p>
                  <p className="text-lg font-semibold text-[#385C4B]">
                    {preview.totals.fresh}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Will mark DELIVERED
                  </p>
                  <p className="text-lg font-semibold text-blue-700">
                    {preview.totals.will_flip_pending || 0}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Resend needed
                  </p>
                  <p className="text-lg font-semibold text-purple-700">
                    {preview.totals.resend_needed || 0}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-[#6B6B61]">
                    Already delivered
                  </p>
                  <p className="text-lg font-semibold text-amber-700">
                    {preview.totals.duplicates}
                  </p>
                </div>
              </div>

              <p className="text-xs text-[#6B6B61] font-mono">
                <span className="text-[#385C4B] font-semibold">Fresh</span> ={" "}
                naya green card banega ·{" "}
                <span className="text-blue-700 font-semibold">
                  Will mark DELIVERED
                </span>{" "}
                = pending green ho jaega ·{" "}
                <span className="text-purple-700 font-semibold">
                  Resend needed
                </span>{" "}
                = DAC empty hai, resend list me jaega ·{" "}
                <span className="text-amber-700 font-semibold">
                  Already delivered
                </span>{" "}
                = skip
              </p>

              {/* Buckets */}
              {(preview.buckets || []).map((b, idx) => {
                const fresh = b.entries.filter(
                  (e) => (e.db_status || "fresh") === "fresh"
                );
                const willFlip = b.entries.filter(
                  (e) => e.db_status === "will_flip_pending"
                );
                const alreadyDelivered = b.entries.filter(
                  (e) => e.db_status === "already_delivered"
                );
                const currentGid = resolvedGid(b, idx);
                const currentGroupName =
                  availableGroups.find((g) => g.id === currentGid)?.name ||
                  (b.group_id ? b.group_name : "");
                return (
                  <div
                    key={`${b.group_id}-${b.delivered_date}-${idx}`}
                    className={`border rounded p-3 ${
                      currentGid
                        ? "border-[#DCDCD3] bg-white"
                        : "border-amber-400 bg-amber-50"
                    }`}
                    data-testid={`delivered-bucket-${idx}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded bg-[#385C4B] text-white text-xs font-mono">
                          {b.delivered_date}
                        </span>
                        {currentGid ? (
                          <span className="text-sm font-semibold text-[#1C1C19]">
                            {currentGroupName}
                          </span>
                        ) : (
                          <span className="text-sm font-semibold text-amber-800">
                            Unmatched: "{b.agency_label || "(no agency)"}"
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {fresh.length > 0 && (
                          <span className="text-[#385C4B] font-semibold">
                            {fresh.length} fresh
                          </span>
                        )}
                        {willFlip.length > 0 && (
                          <span className="text-blue-700 font-semibold">
                            {willFlip.length} flip
                          </span>
                        )}
                        {alreadyDelivered.length > 0 && (
                          <span className="text-amber-700 font-semibold">
                            {alreadyDelivered.length} skip
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Route-override dropdown when unmatched */}
                    {!currentGid && (
                      <div className="mb-3">
                        <label className="text-[10px] font-mono uppercase text-[#6B6B61]">
                          Route to group
                        </label>
                        <select
                          value={routeOverride[idx] || ""}
                          onChange={(e) =>
                            setRouteOverride((r) => ({
                              ...r,
                              [idx]: e.target.value,
                            }))
                          }
                          data-testid={`delivered-bucket-${idx}-route`}
                          className="w-full mt-1 px-2 py-1 text-sm border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
                        >
                          <option value="">— Select group —</option>
                          {availableGroups.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Compact table */}
                    <div className="overflow-x-auto max-h-64 border border-[#DCDCD3] rounded">
                      <table className="w-full text-xs font-mono">
                        <thead className="bg-[#F5F5F0] text-[#6B6B61] sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">LPG ID</th>
                            <th className="px-2 py-1 text-left">DAC</th>
                            <th className="px-2 py-1 text-left">Mobile</th>
                            <th className="px-2 py-1 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {b.entries.map((e, i) => {
                            let rowClass = "";
                            let label = null;
                            let tooltip = "";
                            if (e.db_status === "will_flip_pending") {
                              rowClass = "bg-blue-50 text-blue-800";
                              label = (
                                <span>
                                  Will mark DELIVERED
                                  {e.source_agency &&
                                    ` (${e.source_agency})`}
                                </span>
                              );
                              const dispatchDate = (e.source_dispatch_at || "").slice(0, 10);
                              tooltip = [
                                `Existing pending row will be flipped to DELIVERED.`,
                                dispatchDate && `Originally dispatched: ${dispatchDate}`,
                                e.source_agency && `Original agency: ${e.source_agency}`,
                                e.last_sent_uploader && `Uploader: ${e.last_sent_uploader}`,
                                `Delivery date being applied: ${b.delivered_date}`,
                              ].filter(Boolean).join("\n");
                            } else if (e.db_status === "already_delivered") {
                              rowClass = "bg-amber-50 text-amber-800";
                              label = (
                                <span>
                                  Already delivered
                                  {e.last_sent_agency &&
                                    ` (${e.last_sent_agency})`}
                                </span>
                              );
                              const dispatchDate = (e.last_sent_at || "").slice(0, 10);
                              tooltip = [
                                `Already marked delivered — will be skipped.`,
                                dispatchDate && `Original dispatch: ${dispatchDate}`,
                                e.last_sent_agency && `Agency: ${e.last_sent_agency}`,
                                e.last_marked_by && `Marked by: ${e.last_marked_by}`,
                              ].filter(Boolean).join("\n");
                            } else {
                              label = (
                                <span className="text-[#385C4B]">Fresh</span>
                              );
                              tooltip = `New delivered card will be posted on ${b.delivered_date}.`;
                            }
                            return (
                              <tr
                                key={`${e.lpg_id}-${i}`}
                                className={rowClass}
                                title={tooltip}
                                data-testid={`delivered-row-${e.db_status || "fresh"}`}
                              >
                                <td className="px-2 py-1">{e.lpg_id}</td>
                                <td className="px-2 py-1">{e.dac_code}</td>
                                <td className="px-2 py-1">
                                  {e.mobile || "—"}
                                </td>
                                <td className="px-2 py-1">{label}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}

              {/* Resend buckets — rows whose DAC was empty in the CSV.
                  These will be filed into `resend_entries` (or bump an
                  existing pending ticket's attempts) instead of posting
                  a delivered card. Route-override dropdown appears when
                  the agency didn't auto-match a group. */}
              {(preview.resend_buckets || []).length > 0 && (
                <div className="space-y-2">
                  <h3
                    className="text-sm font-semibold text-purple-800"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    Resend Needed · DAC missing rows
                  </h3>
                  {(preview.resend_buckets || []).map((rb, ridx) => {
                    const currentGid =
                      rb.group_id ||
                      routeOverride[`resend:${rb.agency_label || "_"}`] ||
                      "";
                    const currentGroupName =
                      availableGroups.find((g) => g.id === currentGid)?.name ||
                      (rb.group_id ? rb.group_name : "");
                    return (
                      <div
                        key={`resend-${ridx}`}
                        className={`border rounded p-3 ${
                          currentGid
                            ? "border-purple-200 bg-purple-50/40"
                            : "border-amber-400 bg-amber-50"
                        }`}
                        data-testid={`delivered-resend-bucket-${ridx}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded bg-purple-700 text-white text-xs font-mono">
                              RESEND
                            </span>
                            {currentGid ? (
                              <span className="text-sm font-semibold text-[#1C1C19]">
                                {currentGroupName}
                              </span>
                            ) : (
                              <span className="text-sm font-semibold text-amber-800">
                                Unmatched: "{rb.agency_label || "(no agency)"}"
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-purple-700 font-semibold">
                            {rb.entries.length} rows
                          </span>
                        </div>
                        {!currentGid && (
                          <div className="mb-3">
                            <label className="text-[10px] font-mono uppercase text-[#6B6B61]">
                              Route to group
                            </label>
                            <select
                              value={
                                routeOverride[
                                  `resend:${rb.agency_label || "_"}`
                                ] || ""
                              }
                              onChange={(e) =>
                                setRouteOverride((r) => ({
                                  ...r,
                                  [`resend:${rb.agency_label || "_"}`]:
                                    e.target.value,
                                }))
                              }
                              data-testid={`delivered-resend-bucket-${ridx}-route`}
                              className="w-full mt-1 px-2 py-1 text-sm border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
                            >
                              <option value="">— Select group —</option>
                              {availableGroups.map((g) => (
                                <option key={g.id} value={g.id}>
                                  {g.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="overflow-x-auto max-h-64 border border-[#DCDCD3] rounded">
                          <table className="w-full text-xs font-mono">
                            <thead className="bg-[#F5F5F0] text-[#6B6B61] sticky top-0">
                              <tr>
                                <th className="px-2 py-1 text-left">LPG ID</th>
                                <th className="px-2 py-1 text-left">Mobile</th>
                                <th className="px-2 py-1 text-left">Status</th>
                                <th className="px-2 py-1 text-left">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rb.entries.map((e, i) => (
                                <tr
                                  key={`${e.lpg_id}-${i}`}
                                  className={
                                    e.resend_status === "will_retry"
                                      ? "bg-amber-50 text-amber-800"
                                      : "text-purple-800"
                                  }
                                  title={
                                    e.resend_status === "will_retry"
                                      ? `Existing pending ticket · attempts ${e.existing_attempts || 1}/5 · will bump attempts`
                                      : "New resend ticket will be filed"
                                  }
                                >
                                  <td className="px-2 py-1">{e.lpg_id}</td>
                                  <td className="px-2 py-1">
                                    {e.mobile || "—"}
                                  </td>
                                  <td className="px-2 py-1">
                                    {e.resend_status === "will_retry"
                                      ? `Retry #${(e.existing_attempts || 1) + 1}`
                                      : "New ticket"}
                                  </td>
                                  <td className="px-2 py-1 truncate max-w-[220px]">
                                    {e.reason}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {phase === "committing" && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#385C4B]" />
              <p className="text-sm text-[#6B6B61] mt-3 font-mono">
                Posting delivered cards…
              </p>
            </div>
          )}

          {phase === "done" && commitRes && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[#385C4B]">
                <CheckCircle2 className="w-5 h-5" />
                <p className="font-semibold">Delivered import complete</p>
              </div>
              <table className="w-full text-sm border border-[#DCDCD3]">
                <thead className="bg-[#F5F5F0]">
                  <tr>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-left">Group</th>
                    <th className="px-2 py-1 text-right">Fresh</th>
                    <th className="px-2 py-1 text-right">Flipped</th>
                    <th className="px-2 py-1 text-right">Skipped</th>
                    <th className="px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(commitRes.results || []).map((r, i) => {
                    const gname =
                      availableGroups.find((g) => g.id === r.group_id)?.name ||
                      r.group_id;
                    return (
                      <tr
                        key={`res-${i}`}
                        className={r.ok ? "" : "bg-red-50 text-red-700"}
                      >
                        <td className="px-2 py-1 font-mono">
                          {r.delivered_date}
                        </td>
                        <td className="px-2 py-1">{gname}</td>
                        <td className="px-2 py-1 text-right">{r.sent || 0}</td>
                        <td className="px-2 py-1 text-right text-blue-700">
                          {r.flipped || 0}
                        </td>
                        <td className="px-2 py-1 text-right text-amber-700">
                          {r.already_delivered || 0}
                        </td>
                        <td className="px-2 py-1">
                          {r.ok
                            ? r.skipped_all_duplicate
                              ? "All skipped"
                              : "Posted"
                            : r.error}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2">
          {phase === "ready" && preview && (
            <button
              onClick={handleCommit}
              disabled={
                (preview.totals.fresh || 0) +
                  (preview.totals.will_flip_pending || 0) +
                  (preview.totals.resend_needed || 0) ===
                0
              }
              data-testid="delivered-modal-commit"
              className="px-4 py-2 bg-[#385C4B] text-white rounded text-sm font-semibold hover:bg-[#2c4a3b] disabled:bg-[#DCDCD3] disabled:text-[#6B6B61] disabled:cursor-not-allowed flex items-center gap-2"
            >
              <PackageCheck className="w-4 h-4" />
              {(() => {
                const f = preview.totals.fresh || 0;
                const fp = preview.totals.will_flip_pending || 0;
                const rn = preview.totals.resend_needed || 0;
                const parts = [];
                if (f) parts.push(`${f} fresh`);
                if (fp) parts.push(`${fp} flip`);
                if (rn) parts.push(`${rn} resend`);
                return `Import · ${parts.join(" + ")}`;
              })()}
            </button>
          )}
          {phase === "done" && (
            <button
              onClick={onClose}
              data-testid="delivered-modal-done"
              className="px-4 py-2 bg-[#385C4B] text-white rounded text-sm font-semibold hover:bg-[#2c4a3b]"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
